// ============================================
// App - Main Application Component
// Design System v2.0
// ============================================

import { useState, useEffect } from 'preact/hooks';
import { AnimatePresence } from 'framer-motion';
import { ErrorBoundary, ToastContainer, OfflineIndicator } from '@/components/ui';
import { LevelSelector } from '@/components/LevelSelector';
import { StudyMode } from '@/components/StudyMode';
import { QuizMode } from '@/components/QuizMode';
import { LevelProgress } from '@/components/LevelProgress';
import { FooterActions } from '@/components/FooterActions';
import { FeedbackModal, LearningInfoModal } from '@/components/modals';
import { loadVocabularyForLevel, loadParticlesForLevel, loadGrammarForLevel, LEVEL_CONFIG } from '@/data';
import { useMountEffect } from '@/hooks/useMountEffect';
import {
  currentLevel,
  currentMode,
  currentQuizMode,
  deck,
  setMode,
  setQuizMode,
  setDeck,
  setLevel,
  hydrateProgressForLevel,
  rebuildDueDeck,
  setWordProgressMap,
  notifications,
  dismissNotification,
  showNotification
} from '@/state/signals';
import { STORAGE_CONFIG } from '@/constants';
import type { Mode, Particle, GrammarPattern, JLPTLevel, LevelInfo, WordProgressMap } from '@/types';
import { availableQuizModesForLevel } from '@/types';

// ── Cloud-sync UI (conditionally rendered; Supabase chunk stays lazy) ──
// Static import is fine: Vite inlines import.meta.env at build time and
// tree-shakes dead branches when VITE_FEATURE_CLOUD_SYNC !== 'true'.
import { AccountMenu } from '@/components/AccountMenu';
import { OnboardingHint } from '@/components/OnboardingHint';

// ============================================
// Mode Tabs Component
// ============================================

interface ModeTabsProps {
  currentMode: Mode;
  onChangeMode: (mode: Mode) => void;
}

function ModeTabs({ currentMode, onChangeMode }: ModeTabsProps) {
  const modes: { mode: Mode; label: string; icon: string }[] = [
    { mode: 'study', label: 'Study', icon: '📚' },
    { mode: 'quiz', label: 'Quiz', icon: '✍️' }
  ];

  return (
    <div className="flex justify-center gap-2 mb-6" role="tablist">
      {modes.map(({ mode, label, icon }) => (
        <button
          key={mode}
          type="button"
          role="tab"
          aria-selected={currentMode === mode}
          onClick={() => onChangeMode(mode)}
          className={`
            px-6 py-3 rounded-xl font-semibold text-lg
            transition-all duration-200
            ${currentMode === mode
              ? 'btn-primary shadow-2'
              : 'bg-white text-slate-600 hover:bg-slate-50 shadow-1 border border-slate-200'
            }
          `}
        >
          <span className="mr-2">{icon}</span>
          {label}
        </button>
      ))}
    </div>
  );
}

// ============================================
// Loading State Component
// ============================================

function LoadingState() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-500 font-medium">Loading vocabulary...</p>
      </div>
    </div>
  );
}

// ============================================
// Error State Component
// ============================================

interface ErrorStateProps {
  error: Error;
  onRetry: () => void;
}

function ErrorState({ error, onRetry }: ErrorStateProps) {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center p-8 bg-red-50 rounded-2xl border border-red-100 max-w-md">
        <div className="text-4xl mb-4">😔</div>
        <h2 className="text-xl font-bold text-red-800 mb-2">Failed to load</h2>
        <p className="text-red-600 mb-4">{error.message}</p>
        <button
          onClick={onRetry}
          className="px-5 py-2.5 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-lg
                     font-semibold hover:shadow-md transition-shadow"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

// ============================================
// Main App Component
// ============================================

export function App() {
  const level = currentLevel.value;
  const mode = currentMode.value;
  const toasts = notifications.value;
  const words = deck.value;

  const [particles, setParticles] = useState<Particle[]>([]);
  const [grammar, setGrammar] = useState<GrammarPattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [levelDropdownOpen, setLevelDropdownOpen] = useState(false);
  // Bumped by handleRetry to re-fire the level-load effect with the
  // same cancellation guards that protect the initial load.
  const [retryNonce, setRetryNonce] = useState(0);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isLearningInfoOpen, setIsLearningInfoOpen] = useState(false);

  // Build level info for selector
  const levels: LevelInfo[] = Object.entries(LEVEL_CONFIG).map(([key, config]) => ({
    level: key as JLPTLevel,
    displayName: config.displayName,
    description: config.description,
    color: config.color,
    wordCount: words.length,
    enabled: config.enabled
  }));

  // Load vocabulary + hydrate per-level progress when level changes.
  // Order matters:
  //   1. hydrateProgressForLevel(level): replace wordProgress with the
  //      newly-active level's persisted state (Finding H — without this
  //      the previous level's state leaks in-memory across switches).
  //   2. setDeck(vocabData): updates `deck` signal AND triggers
  //      rebuildDueDeck() so the SRS-aware queue reflects the new level.
  //
  // Two ordering guards:
  //   - `cancelled` flag set in cleanup blocks stale async resolutions.
  //   - currentLevel.peek() === level re-check inside the apply block
  //     blocks the race where Promise.all resolves AFTER the user
  //     switched level but BEFORE the cleanup ran. Without this guard
  //     a slow N5 load could land vocab+progress against a fresh N4
  //     selection.
  // eslint-disable-next-line no-restricted-syntax
  useEffect(() => {
    let cancelled = false;
    const targetLevel = level;

    async function loadData() {
      setLoading(true);
      setError(null);

      try {
        const [vocabData, particleData, grammarData] = await Promise.all([
          loadVocabularyForLevel(targetLevel),
          loadParticlesForLevel(targetLevel),
          loadGrammarForLevel(targetLevel)
        ]);

        if (cancelled || currentLevel.peek() !== targetLevel) return;

        // Reset quiz mode BEFORE setDeck so the post-render won't briefly
        // route to a mode this level doesn't offer (e.g. particle-quiz carried
        // over from N4 into N3, or into Everyday which has no fill-blank mode)
        // between setDeck firing rebuildDueDeck and setQuizMode landing.
        if (!availableQuizModesForLevel(targetLevel).includes(currentQuizMode.value)) {
          setQuizMode('multiple-choice');
        }

        hydrateProgressForLevel(targetLevel);
        setParticles(particleData);
        setGrammar(grammarData);
        setDeck(vocabData); // also rebuilds dueDeck
      } catch (err) {
        if (cancelled || currentLevel.peek() !== targetLevel) return;
        console.error('Failed to load vocabulary:', err);
        setError(err instanceof Error ? err : new Error('Failed to load vocabulary'));
      } finally {
        if (!cancelled && currentLevel.peek() === targetLevel) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [level, retryNonce]);

  // Multi-tab storage race: when another tab writes the active level's
  // progress map, re-hydrate ours and rebuild the due deck. Without this,
  // two open tabs silently clobber each other's progress. The
  // setWordProgressMap helper is required (not bare wordProgress.value =
  // map) because the four counter signals are derived state — every
  // map-replacing write must pair with a count sync.
  // eslint-disable-next-line no-restricted-syntax
  useEffect(() => {
    const expectedKey = STORAGE_CONFIG.keys.wordProgress(level);
    const onStorage = (e: StorageEvent) => {
      if (e.key !== expectedKey || e.newValue == null) return;
      try {
        setWordProgressMap(JSON.parse(e.newValue) as WordProgressMap);
        rebuildDueDeck();
      } catch (err) {
        console.warn('Failed to parse cross-tab storage update', err);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [level]);

  // Close dropdown when clicking outside
  // eslint-disable-next-line no-restricted-syntax
  useEffect(() => {
    if (!levelDropdownOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-level-selector]')) {
        setLevelDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [levelDropdownOpen]);

  const handleLevelChange = (newLevel: JLPTLevel) => {
    setLevel(newLevel);
    setLevelDropdownOpen(false);
  };

  // Re-fire the level-load effect via the retryNonce dep — reuses the
  // same cancellation + level-equality guards as the initial load
  // instead of duplicating the async logic with weaker protection.
  const handleRetry = () => {
    setError(null);
    setRetryNonce(n => n + 1);
  };

  // ── Cloud-sync: idle-deferred init ────────────────────────────────────
  // Dynamically import + start SyncService after the first paint so the
  // Supabase chunk does NOT block the initial render. useMountEffect runs
  // once; requestIdleCallback (with setTimeout fallback) defers further.
  if (import.meta.env.VITE_FEATURE_CLOUD_SYNC === 'true') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useMountEffect(() => {
      let ran = false;
      const run = () => {
        if (ran) return;
        ran = true;
        // Sync init failing must not be silent — the chunk can fail to load
        // (offline/blocked) or initSync can throw. Log it and nudge the user.
        void import('@/services/SyncService')
          .then(({ initSync }) => initSync())
          .catch(err => {
            console.error('Cloud sync failed to initialize', err);
            showNotification('Cloud sync is unavailable right now.', 'warning', 4000);
          });
      };
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(run, { timeout: 3000 });
      } else {
        setTimeout(run, 200);
      }
    });
  }

  // ── Cloud-sync: visibility/focus pull ─────────────────────────────────
  // Mirrors the existing storage-listener pattern: external-sync effect,
  // so eslint-disable-next-line is the appropriate escape hatch here.
  if (import.meta.env.VITE_FEATURE_CLOUD_SYNC === 'true') {
    // eslint-disable-next-line react-hooks/rules-of-hooks, no-restricted-syntax
    useEffect(() => {
      const pull = () => {
        // Background pull on focus/visibility — log a load/pull failure rather
        // than toasting on every focus (which would be noisy for a periodic op).
        void import('@/services/SyncService')
          .then(({ getSyncService }) => {
            getSyncService()?.maybePullActiveLevel();
          })
          .catch(err => console.error('Cloud sync pull failed', err));
      };

      const onVisibility = () => {
        if (document.visibilityState === 'visible') pull();
      };

      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('focus', pull);
      return () => {
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('focus', pull);
      };
    }, []);
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen pb-8 safe-bottom" style={{ background: 'var(--color-surface)' }}>
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 mb-6 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <h1 className="text-2xl font-bold text-gradient-primary font-japanese">
                日本語楽しみ
              </h1>
              {/* Right side: LevelSelector + AccountMenu (cloud-sync only) */}
              <div className="flex items-center gap-3">
                <div data-level-selector>
                  <LevelSelector
                    levels={levels}
                    currentLevel={level}
                    isOpen={levelDropdownOpen}
                    onToggle={() => setLevelDropdownOpen(!levelDropdownOpen)}
                    onSelect={handleLevelChange}
                  />
                </div>
                {/* AccountMenu renders null when flag is off */}
                <div className="relative" data-account-menu>
                  <AccountMenu />
                  {/* OnboardingHint renders null when flag is off / logged in / no progress */}
                  <OnboardingHint />
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4">
          {/* Mode Tabs */}
          <ModeTabs currentMode={mode} onChangeMode={setMode} />

          {/* Persistent per-level progress (self-hides until deck loads) */}
          {!loading && !error && <LevelProgress />}

          {/* Content Area */}
          {loading && <LoadingState />}

          {error && <ErrorState error={error} onRetry={handleRetry} />}

          {!loading && !error && (
            <AnimatePresence mode="wait">
              {mode === 'study' ? (
                <StudyMode key="study" />
              ) : (
                <QuizMode key="quiz" allWords={words} particles={particles} grammar={grammar} />
              )}
            </AnimatePresence>
          )}
        </main>

        {/* Footer Actions */}
        <div className="max-w-4xl mx-auto px-4">
          <FooterActions
            onLearningInfoClick={() => setIsLearningInfoOpen(true)}
            onFeedbackClick={() => setIsFeedbackOpen(true)}
          />
        </div>

        {/* Footer */}
        <footer className="text-center text-slate-400 text-sm mt-8 px-4 pb-4">
          <p>
            Made with <span className="text-rose-400">&#x2665;</span> for Japanese learners
          </p>
          <p className="mt-1">
            <a
              href="https://nihongotanoshimi.com"
              className="hover:text-primary-500 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              nihongotanoshimi.com
            </a>
          </p>
        </footer>

        {/* Modals */}
        <FeedbackModal
          isOpen={isFeedbackOpen}
          onClose={() => setIsFeedbackOpen(false)}
        />
        <LearningInfoModal
          isOpen={isLearningInfoOpen}
          onClose={() => setIsLearningInfoOpen(false)}
        />

        {/* Toast Notifications */}
        <ToastContainer notifications={toasts} onDismiss={dismissNotification} />

        {/* Offline Indicator */}
        <OfflineIndicator />
      </div>
    </ErrorBoundary>
  );
}

export default App;
